/**
 * CRMWhatsApp
 * Super Admin — CRM > WhatsApp
 *
 * Tab 0: Send WhatsApp — contact search/select, template picker, message, media, send
 * Tab 1: WhatsApp Logs — filterable paginated table
 * Tab 2: Templates     — list, create, delete
 *
 * Color scheme: #25D366 WhatsApp green for accents + purple CRM theme
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search,
  X,
  Plus,
  Send,
  Trash2,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Users,
  FileText,
  Clock,
  Image,
  MessageCircle,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const WA_GREEN = '#25D366';

const authHeaders = (contentType = true) => ({
  Authorization: 'Bearer ' + localStorage.getItem('session_token'),
  ...(contentType ? { 'Content-Type': 'application/json' } : {}),
});

// ── WhatsApp Status config ─────────────────────────────────────
const WA_STATUS = {
  queued:    { label: 'Queued',    cls: 'bg-gray-100 text-gray-700',      dot: '#94A3B8' },
  sent:      { label: 'Sent',      cls: 'bg-blue-100 text-blue-700',      dot: '#3B82F6' },
  delivered: { label: 'Delivered', cls: 'bg-yellow-100 text-yellow-800',  dot: '#F59E0B' },
  read:      { label: 'Read',      cls: 'bg-green-100 text-green-700',    dot: '#22C55E' },
  failed:    { label: 'Failed',    cls: 'bg-red-100 text-red-700',        dot: '#EF4444' },
  replied:   { label: 'Replied',   cls: 'bg-purple-100 text-purple-700',  dot: '#A855F7' },
};

const WaStatusBadge = ({ status }) => {
  const key = (status || '').toLowerCase();
  const cfg = WA_STATUS[key] || { label: status || '—', cls: 'bg-gray-100 text-gray-600', dot: '#94A3B8' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${cfg.cls}`}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, display: 'inline-block', flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

// ── Detect {{variables}} ───────────────────────────────────────
const detectVariables = (text) => {
  const matches = [...(text || '').matchAll(/\{\{(\w+)\}\}/g)];
  return [...new Set(matches.map((m) => m[1]))];
};

// ── WhatsApp Message Bubble Preview ───────────────────────────
const WaBubble = ({ message, mediaUrl }) => (
  <div className="flex justify-end">
    <div
      style={{ maxWidth: '85%', background: '#DCF8C6', borderRadius: '12px 0 12px 12px', padding: '10px 14px', position: 'relative' }}
      className="shadow-sm"
    >
      {mediaUrl && (
        <div className="mb-2 flex items-center gap-2 text-xs text-gray-500 bg-white/60 rounded-lg px-2 py-1.5">
          <Image size={14} style={{ color: WA_GREEN }} />
          <span className="truncate max-w-xs">{mediaUrl}</span>
        </div>
      )}
      <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{message || <span className="text-gray-400 italic">Your message preview</span>}</p>
      <p className="text-right text-xs mt-1" style={{ color: '#92A8A0' }}>
        {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        &nbsp;✓✓
      </p>
    </div>
  </div>
);

// ── Reusable Contact Search Picker ────────────────────────────
const ContactPicker = ({ selected, onAdd, onRemove }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (val) => {
    setQuery(val);
    clearTimeout(debounceRef.current);
    if (!val.trim()) { setResults([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API_BASE}/api/crm/contacts/search?q=${encodeURIComponent(val)}`, { headers: authHeaders(false) });
        const data = await res.json();
        setResults(data.contacts || data || []);
        setShowDropdown(true);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
  };

  const selectedIds = new Set(selected.map((c) => c.id || c._id));

  return (
    <div ref={wrapRef} className="space-y-3">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search contacts by name, mobile, org..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-green-400"
        />
        {searching && <RefreshCw size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />}

        {showDropdown && results.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
            {results.map((c) => {
              const id = c.id || c._id;
              const already = selectedIds.has(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => { if (!already) { onAdd(c); setQuery(''); setShowDropdown(false); } }}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${already ? 'opacity-50 cursor-default' : 'hover:bg-green-50'}`}
                >
                  <span>
                    <span className="font-medium text-gray-800">{c.contact_person || c.name || '—'}</span>
                    <span className="text-gray-400 ml-2">{c.mobile_number || c.mobile || ''}</span>
                    <span className="text-gray-400 ml-2 text-xs">{c.organization_name || ''}</span>
                  </span>
                  {already && <span className="text-xs text-gray-400">Added</span>}
                </button>
              );
            })}
          </div>
        )}
        {showDropdown && results.length === 0 && !searching && query.trim() && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-sm text-gray-400">No contacts found</div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((c) => {
            const id = c.id || c._id;
            return (
              <span key={id} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full" style={{ background: '#DCFCE7', color: '#166534' }}>
                {c.contact_person || c.name || c.mobile_number || id}
                <button type="button" onClick={() => onRemove(id)} className="hover:text-red-600 transition-colors">
                  <X size={12} />
                </button>
              </span>
            );
          })}
          <button type="button" onClick={() => onRemove('__all__')} className="text-xs text-gray-400 hover:text-red-500 underline underline-offset-2 transition-colors">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

// ── Tab 0: Send WhatsApp ───────────────────────────────────────
const SendWATab = ({ templates, onSent }) => {
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [message, setMessage] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [isBulk, setIsBulk] = useState(false);

  const variables = detectVariables(message);

  const handleAddContact = (c) => setSelectedContacts((prev) => [...prev, c]);
  const handleRemoveContact = (id) => {
    if (id === '__all__') { setSelectedContacts([]); return; }
    setSelectedContacts((prev) => prev.filter((c) => (c.id || c._id) !== id));
  };

  const applyTemplate = (tplId) => {
    setSelectedTemplate(tplId);
    const tpl = templates.find((t) => String(t.id || t._id) === String(tplId));
    if (tpl) setMessage(tpl.template_content || tpl.content || '');
  };

  const handleSend = async () => {
    if (!message.trim()) { alert('Please enter a message.'); return; }
    if (selectedContacts.length === 0) { alert('Please select at least one contact.'); return; }
    setSending(true);
    try {
      const isBulkSend = selectedContacts.length > 1 || isBulk;
      const endpoint = isBulkSend
        ? `${API_BASE}/api/crm/whatsapp/bulk`
        : `${API_BASE}/api/crm/whatsapp/send`;

      const body = isBulkSend
        ? { contact_ids: selectedContacts.map((c) => c.id || c._id), message, media_url: mediaUrl || undefined }
        : { contact_id: selectedContacts[0].id || selectedContacts[0]._id, mobile: selectedContacts[0].mobile_number || selectedContacts[0].mobile, message, media_url: mediaUrl || undefined };

      const res = await fetch(endpoint, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      onSent('WhatsApp message sent successfully!');
      setSelectedContacts([]);
      setMessage('');
      setMediaUrl('');
      setSelectedTemplate('');
    } catch (e) {
      onSent(null, 'Send failed: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Left: form (3/5) */}
      <div className="lg:col-span-3 space-y-6">
        {/* Recipients */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <Users size={18} style={{ color: WA_GREEN }} />
              Recipients
            </h3>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={isBulk} onChange={(e) => setIsBulk(e.target.checked)} className="accent-green-500" />
              Bulk mode
            </label>
          </div>
          <ContactPicker selected={selectedContacts} onAdd={handleAddContact} onRemove={handleRemoveContact} />
          {selectedContacts.length > 0 && (
            <p className="mt-2 text-xs text-gray-400">{selectedContacts.length} recipient{selectedContacts.length !== 1 ? 's' : ''} selected</p>
          )}
        </div>

        {/* Message Composer */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <MessageCircle size={18} style={{ color: WA_GREEN }} />
            Message
          </h3>

          {/* Template selector */}
          {templates.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Use Template</label>
              <select
                value={selectedTemplate}
                onChange={(e) => applyTemplate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-green-400"
              >
                <option value="">— Select a template —</option>
                {templates.map((t) => (
                  <option key={t.id || t._id} value={t.id || t._id}>{t.template_name || t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Message textarea */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Message Content</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="Hello {{contact_person}}, we have an update for {{organization_name}}..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:border-green-400"
            />
            {variables.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                {variables.map((v) => (
                  <span key={v} className="text-xs px-2 py-0.5 rounded-md font-mono" style={{ background: '#DCFCE7', color: '#166534' }}>{`{{${v}}}`}</span>
                ))}
                <span className="text-xs text-gray-400">will be personalized per contact</span>
              </div>
            )}
          </div>

          {/* Media URL */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide flex items-center gap-1.5">
              <Image size={12} />
              Media URL (optional)
            </label>
            <input
              type="url"
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              placeholder="https://example.com/image.jpg or .pdf"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-green-400"
            />
            <p className="text-xs text-gray-400 mt-1">Attach an image or PDF URL to send alongside the message</p>
          </div>

          {/* Send button */}
          <div className="flex justify-end pt-1">
            <button
              onClick={handleSend}
              disabled={sending || !message.trim() || selectedContacts.length === 0}
              className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: WA_GREEN }}
            >
              {sending ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? 'Sending...' : `Send WhatsApp${selectedContacts.length > 1 ? ` to ${selectedContacts.length}` : ''}`}
            </button>
          </div>
        </div>
      </div>

      {/* Right: Preview (2/5) */}
      <div className="lg:col-span-2">
        <div className="sticky top-6 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* WhatsApp header bar */}
          <div className="px-4 py-3 flex items-center gap-3" style={{ background: WA_GREEN }}>
            <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center">
              <Users size={16} className="text-white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm leading-tight">
                {selectedContacts.length > 0
                  ? (selectedContacts[0].contact_person || selectedContacts[0].name || selectedContacts[0].mobile_number || 'Contact')
                  : 'Preview'}
              </p>
              <p className="text-white/70 text-xs">online</p>
            </div>
          </div>

          {/* Chat area */}
          <div className="p-4 min-h-52 space-y-3" style={{ background: '#E5DDD5' }}>
            {(message || mediaUrl) ? (
              <WaBubble message={message} mediaUrl={mediaUrl} />
            ) : (
              <div className="flex items-center justify-center h-40 text-center">
                <p className="text-sm text-gray-500 bg-white/70 px-4 py-2 rounded-xl">Message preview will appear here</p>
              </div>
            )}
          </div>

          {/* Input bar mock */}
          <div className="px-3 py-2 border-t border-gray-200 flex items-center gap-2 bg-white">
            <div className="flex-1 bg-gray-100 rounded-full px-4 py-2 text-xs text-gray-400 select-none">Type a message</div>
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: WA_GREEN }}>
              <Send size={14} className="text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Tab 1: WhatsApp Logs ───────────────────────────────────────
const WALogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const LIMIT = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (statusFilter) params.set('status', statusFilter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await fetch(`${API_BASE}/api/crm/whatsapp/logs?${params}`, { headers: authHeaders(false) });
      const data = await res.json();
      setLogs(data.logs || data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('WA logs fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const truncate = (str, n = 55) => str && str.length > n ? str.slice(0, n) + '…' : str;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-green-400"
          >
            <option value="">All Statuses</option>
            {Object.entries(WA_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">From</label>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-green-400" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">To</label>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-green-400" />
        </div>
        <button onClick={fetchLogs} className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:border-green-500 hover:text-green-600 transition-colors">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Mobile', 'Contact', 'Message', 'Status', 'Sent At', 'Read At', 'Reply'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center">
                  <RefreshCw size={22} className="animate-spin mx-auto" style={{ color: WA_GREEN }} />
                </td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-sm">No WhatsApp logs found</td></tr>
              ) : logs.map((log, i) => (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-700 font-mono text-xs">{log.mobile || log.mobile_number || '—'}</td>
                  <td className="px-4 py-3 text-gray-800 font-medium">{log.contact_name || log.name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs" title={log.message}>{truncate(log.message)}</td>
                  <td className="px-4 py-3"><WaStatusBadge status={log.status} /></td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{log.sent_at ? new Date(log.sent_at).toLocaleString('en-IN') : '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{log.read_at ? new Date(log.read_at).toLocaleString('en-IN') : '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate" title={log.reply}>{log.reply || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-500">Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total}</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 transition-colors">
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1 text-sm font-semibold text-gray-700">{page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 transition-colors">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Tab 2: Templates ───────────────────────────────────────────
const WATemplatesTab = ({ templates, onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ template_name: '', template_content: '', variables: '' });

  const detectedVars = detectVariables(form.template_content);

  const handleCreate = async () => {
    if (!form.template_name.trim() || !form.template_content.trim()) { alert('Name and content are required.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/crm/settings/templates`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...form, template_type: 'whatsapp', variables: detectedVars.join(',') }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      onRefresh('Template created!');
      setShowModal(false);
      setForm({ template_name: '', template_content: '', variables: '' });
    } catch (e) {
      alert('Failed to create template: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this WhatsApp template?')) return;
    setDeleting(id);
    try {
      const res = await fetch(`${API_BASE}/api/crm/whatsapp/templates/${id}`, { method: 'DELETE', headers: authHeaders(false) });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      onRefresh('Template deleted.');
    } catch (e) {
      alert('Failed to delete: ' + e.message);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 text-white rounded-lg transition-colors text-sm font-semibold"
          style={{ background: WA_GREEN }}
        >
          <Plus size={16} />
          New Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-400">
          <FileText size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No WhatsApp templates yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((tpl) => {
            const id = tpl.id || tpl._id;
            const vars = detectVariables(tpl.template_content || tpl.content || '');
            return (
              <div key={id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex flex-col gap-3 hover:border-green-300 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-bold text-gray-800 text-sm">{tpl.template_name || tpl.name}</h4>
                  <button
                    onClick={() => handleDelete(id)}
                    disabled={deleting === id}
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    {deleting === id ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>

                {/* Mini bubble preview */}
                <div className="rounded-xl p-3 text-xs text-gray-700 leading-relaxed whitespace-pre-wrap line-clamp-4" style={{ background: '#DCF8C6' }}>
                  {tpl.template_content || tpl.content}
                </div>

                {vars.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100">
                    {vars.map((v) => (
                      <span key={v} className="text-xs px-2 py-0.5 rounded-md font-mono" style={{ background: '#DCFCE7', color: '#166534' }}>{`{{${v}}}`}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 text-lg">New WhatsApp Template</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Template Name *</label>
                <input
                  type="text"
                  value={form.template_name}
                  onChange={(e) => setForm((f) => ({ ...f, template_name: e.target.value }))}
                  placeholder="e.g. Event Invitation"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-green-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Content *</label>
                <textarea
                  value={form.template_content}
                  onChange={(e) => setForm((f) => ({ ...f, template_content: e.target.value }))}
                  rows={5}
                  placeholder={"Hi {{contact_person}},\n\nYou're invited to {{event_name}} on {{date}}!\n\nRegards,\nSkillGenie Team"}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:border-green-400"
                />
                <p className="text-xs text-gray-400 mt-1">Use <code className="bg-gray-100 px-1 rounded">{'{{variable_name}}'}</code> for dynamic values</p>
                {detectedVars.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2 items-center">
                    <span className="text-xs text-gray-400">Detected:</span>
                    {detectedVars.map((v) => (
                      <span key={v} className="text-xs px-2 py-0.5 rounded-md font-mono" style={{ background: '#DCFCE7', color: '#166534' }}>{`{{${v}}}`}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Live preview in modal */}
              {form.template_content && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Preview</label>
                  <div className="rounded-xl p-3" style={{ background: '#E5DDD5' }}>
                    <WaBubble message={form.template_content} />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 text-white rounded-lg transition-colors text-sm font-semibold disabled:opacity-50"
                style={{ background: WA_GREEN }}
              >
                {saving ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
                {saving ? 'Saving…' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────
const CRMWhatsApp = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [banner, setBanner] = useState(null);

  const showBanner = (type, msg) => {
    setBanner({ type, msg });
    setTimeout(() => setBanner(null), 5000);
  };

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/crm/whatsapp/templates?type=whatsapp`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      const list = data.data?.templates || data.templates || data.data || data || [];
      setTemplates(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('WA templates fetch error:', e);
      showBanner('error', 'Failed to fetch templates: ' + e.message);
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleSent = (successMsg, errorMsg) => {
    if (successMsg) showBanner('success', successMsg);
    if (errorMsg) showBanner('error', errorMsg);
  };

  const handleTemplateRefresh = (msg) => {
    fetchTemplates();
    if (msg) showBanner('success', msg);
  };

  const TABS = [
    { label: 'Send WhatsApp', icon: Send },
    { label: 'Logs',          icon: Clock },
    { label: 'Templates',     icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Banner */}
      {banner && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-semibold max-w-xl w-full ${banner.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          {banner.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span className="flex-1">{banner.msg}</span>
          <button onClick={() => setBanner(null)}><X size={16} /></button>
        </div>
      )}

      <div className="max-w-6xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: WA_GREEN }}>
            <MessageCircle size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">CRM WhatsApp</h1>
            <p className="text-gray-500 text-sm">Send and manage WhatsApp messages to CRM contacts</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border border-gray-200 w-fit">
          {TABS.map((tab, i) => {
            const Icon = tab.icon;
            const isActive = activeTab === i;
            return (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all"
                style={isActive
                  ? { background: WA_GREEN, color: '#fff', boxShadow: '0 1px 4px rgba(37,211,102,0.4)' }
                  : { color: '#4B5563', background: 'transparent' }
                }
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Panels */}
        {activeTab === 0 && (
          <SendWATab templates={templates} onSent={handleSent} />
        )}
        {activeTab === 1 && <WALogsTab />}
        {activeTab === 2 && (
          templatesLoading
            ? <div className="flex justify-center py-10"><RefreshCw size={22} className="animate-spin" style={{ color: WA_GREEN }} /></div>
            : <WATemplatesTab templates={templates} onRefresh={handleTemplateRefresh} />
        )}
      </div>
    </div>
  );
};

export default CRMWhatsApp;
